import React, { useState } from "react";
import { Table, InputNumber, Select, Button, Input } from "antd";
import { PlusOutlined, DeleteOutlined, UploadOutlined, CheckOutlined, DownloadOutlined, ExclamationCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";
import { Tooltip } from "antd";

const { Option } = Select;

const MassiveEmitionTable = ({ company, clients, invoice, modifyInvoice }) => {
	const [count, setCount] = useState(0);

	const handleAdd = () => {
		const newData = {
			key: count,
			amount: 0,
			client: "",
			address: "",
			contacts: [],
		};
		modifyInvoice("receptors", [...invoice.receptors, newData]);
		setCount(count + 1);
	};


	const handleSave = (row) => {
		const newData = [...invoice.receptors];
		const index = newData.findIndex((item) => row.key === item.key);
		const item = newData[index];
		newData.splice(index, 1, { ...item, ...row });
		modifyInvoice("receptors", newData);
	};

	const handleDelete = (key) => {
		const newData = invoice.receptors.filter((item) => item.key !== key);
		modifyInvoice("receptors", newData);
	};

	const handleClientChange = (value, record) => {
		const client = clients.find(client => client.id === value);
		handleSave({
			...record,
			client: value,
			name: client.name,
			id: client.id,
			tid: client.entity_company_tid,
			address: client.address,
			type_tid: "",
			country: "",
			contacts: client.contacts,
			selected_contacts: client.contacts.map(contact => contact.email),
		});
	};

	const columns = [
		{
			title: (
				<span>
					Total
					<Tooltip title="Obligatorio. Debe ser un número.">
						<ExclamationCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "amount",
			align: "center",
			width: "15%",
			render: (text, record) => (
				<InputNumber
					value={record.amount}
					onChange={(value) => handleSave({ ...record, amount: value || 0 })}
					style={{ width: "100%", textAlign: "right" }}
					//formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
					//parser={value => value.replace(/\$\s?|(,*)/g, '')}
					precision={2}
					controls={false}
					disabled={record.success}
				/>
			),
		},
		{
			title: (
				<span>
					Cliente
					<Tooltip title="Obligatorio. Debe ser un nit válido en la plantilla y el nit debe estar asociado como cliente.">
						<ExclamationCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "client",
			align: "center",
			width: "35%",
			render: (text, record) => (
				<Select
					showSearch
					style={{ width: "100%" }}
					value={record.client}
					onChange={(value) => handleClientChange(value, record)}
					disabled={record.success}
					filterOption={(input, option) =>
						option.children.toLowerCase().includes(input.toLowerCase())
					}
				>
					{clients.map(client => (
						<Option key={client.id} value={client.id}>{client.name + " (" + client.entity_company_tid + ")"}</Option>
					))}
				</Select>
			),
		},
		{
			title: (
				<span>
					Dirección
					<Tooltip title="No es obligatorio. En caso esté vacío se colocará automáticamente 'CIUDAD'.">
						<QuestionCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "address",
			align: "center",
			width: "20%",
			render: (text, record) => (
				<Input
					value={record.address}
					onChange={(e) => handleSave({ ...record, address: e.target.value })}
					style={{ width: "100%" }}
					maxLength={100}
					disabled={record.success}
				/>
			),
		},
		{
			title: (
				<span>
					Contactos
					<Tooltip title="Obligatorio. Como mínimo debe haber un correo electrónico, en caso sean más, deben estar separados por ';' en la plantilla.">
						<ExclamationCircleOutlined style={{ marginLeft: 4 }} />
					</Tooltip>
				</span>
			),
			dataIndex: "contacts",
			align: "center",
			width: "20%",
			render: (text, record) => (
				<Select
					mode="multiple"
					style={{ width: "100%"}}
					value={record.selected_contacts}
					onChange={(value) => {
						handleSave({ ...record, selected_contacts: value });
					}}
					options={[...record.contacts || [], ...company.contacts || []].map(contact => (
						{ 
							label: contact.name + " (" + contact.email + ")", 
							value: contact.email 
						}))}
					disabled={record.success}
				>
				</Select>
			),
		},
		...(invoice.receptors.some(r => r.message) ? [{
			title: "Mensaje",
			dataIndex: "message",
			align: "center",
			width: "15%",
			render: (text, record) => (
				record.message && (
					<span style={{ color: record.success ? "green" : "red" }}>
						{record.message}
					</span>
				)
			),
		}] : []),
		{
			title: "",
			dataIndex: "action",
			align: "center",
			width: "5%",
			render: (text, record) => (
				record.success ? 
					<CheckOutlined style={{ color: "green" }} /> : 
					<Button onClick={() => handleDelete(record.key)} type="danger" icon={<DeleteOutlined style={{color:"red"}} />} />
			),
		},
	];

	const handleUploadTemplate = (file) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const data = e.target.result;
			const workbook = XLSX.read(data, { type: "binary" });
			const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
			const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
			const processedData = [];
			const unprocessedData = [];

			// Check if first row contains headers and skip it
			let startIndex = 0;
			if (jsonData.length > 0 && 
				jsonData[0].length >= 4 && 
				String(jsonData[0][0]).toLowerCase().includes("total") &&
				String(jsonData[0][1]).toLowerCase().includes("nit") &&
				(String(jsonData[0][2]).toLowerCase().includes("direccion") || String(jsonData[0][2]).toLowerCase().includes("dirección")) &&
				String(jsonData[0][3]).toLowerCase().includes("correos")) {
				startIndex = 1;
			}

			jsonData.slice(startIndex).forEach((row, index) => {
				// Check if total is a valid number
				const totalValue = parseFloat(row[0]);
				if (isNaN(totalValue)) {
					unprocessedData.push({
						amount: row[0],
						client_tid: row[1] || "",
						address: row[2] || "",
						contacts: row[3] || "",
						reason: "Total inválido: debe ser un número",
					});
					return;
				}

				const client = clients.find(c => c.entity_company_tid === String(row[1]));
				if (client) {
					const contacts = row[3] ? row[3].split(";").map(email => {
						const contact = client.contacts.find(c => c.email === email);
						return { email, name: contact ? contact.name : "" };
					}) : [];

					processedData.push({
						key: count + index,
						amount: totalValue,
						client: client.name + " (" + client.entity_company_tid + ")",
						id: client.id,
						tid: client.entity_company_tid,
						name: client.name,
						type_tid: "",
						country: "",
						address: row[2] || client.address,
						contacts: contacts,
						selected_contacts: contacts.map(contact => contact.email),
					});
				} else {
					unprocessedData.push({
						amount: row[0],
						client_tid: row[1] || "",
						address: row[2] || "",
						contacts: row[3] || "",
						reason: "Cliente no encontrado",
					});
				}
			});

			modifyInvoice("receptors", [...invoice.receptors, ...processedData]);
			setCount(count + processedData.length);

			if (unprocessedData.length > 0) {
				const ws = XLSX.utils.json_to_sheet(unprocessedData);
				const wb = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(wb, ws, "Datos no procesados");
				XLSX.writeFile(wb, "datos_no_procesados.xlsx");
			}
		};
		reader.readAsBinaryString(file);
	};

	const handleDownloadExampleTemplate = () => {
		// Create example data with headers
		const exampleData = [
			["Total", "NIT", "Dirección", "Correos"],
			[1000.50, "900123456", "Calle Principal 123", "ejemplo@correo.com;otro@correo.com"],
			[2500.75, "800987654", "Avenida Central 456", "contacto@empresa.com"],
			[3750.25, "700111222", "Carrera 10 #15-20", "info@compania.com;ventas@compania.com"]
		];

		// Create worksheet from example data
		const ws = XLSX.utils.aoa_to_sheet(exampleData);
		
		// Set column widths for better readability
		const colWidths = [
			{ wch: 15 }, // Total
			{ wch: 20 }, // NIT
			{ wch: 30 }, // Dirección
			{ wch: 40 }  // Correos
		];
		ws["!cols"] = colWidths;

		// Create workbook and append worksheet
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Plantilla");

		// Generate and download file
		XLSX.writeFile(wb, "plantilla_facturacion_masiva.xlsx");
	};

	const showUploadPanel = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = ".xlsx, .xls";
		input.onchange = (e) => {
			const file = e.target.files[0];
			handleUploadTemplate(file);
		};
		input.click();
	};

	return (
		<>
			<Button
				onClick={handleAdd}
				type="primary"
				style={{ marginBottom: 16, marginRight: 10 }}
				icon={<PlusOutlined />}
			>
                Agregar Fila
			</Button>
			<Button
				onClick={showUploadPanel}
				style={{ marginBottom: 16, marginRight: 10 }}
				type="primary"
				icon={<UploadOutlined />}
				ghost
			>
                Subir Plantilla
			</Button>
			<Button
				onClick={handleDownloadExampleTemplate}
				type="default"
				icon={<DownloadOutlined />}
			>
                Descargar Ejemplo
			</Button>
			<Table
				dataSource={invoice.receptors}
				columns={columns}
				rowClassName={() => "editable-row"}
				pagination={false}
				size="small"
				style={{ fontSize: "12px" }}
			/>
		</>
	);
};

MassiveEmitionTable.propTypes = {	
	company: PropTypes.object.isRequired,
	clients: PropTypes.array.isRequired,
	invoice: PropTypes.object.isRequired,
	modifyInvoice: PropTypes.func.isRequired,
};

export default MassiveEmitionTable;